import { motion } from "framer-motion";

/* ─── Premium feature icons ─────────────────────────────────────── */
const QualityIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8">
    <path d="M24 4 L28.5 14.5 L40 15.5 L31.5 23.5 L34 35 L24 29.5 L14 35 L16.5 23.5 L8 15.5 L19.5 14.5 Z"
      fill="#FFF1F2" stroke="#E31837" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M19 24 L22.5 27.5 L29 20" stroke="#E31837" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SpeedIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8">
    <circle cx="24" cy="28" r="16" fill="#FFF1F2" stroke="#E31837" strokeWidth="1.8"/>
    <path d="M14 28 A10 10 0 0 1 34 28" stroke="#E31837" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Speedometer needle */}
    <line x1="24" y1="28" x2="33" y2="21" stroke="#E31837" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="24" cy="28" r="2.5" fill="#E31837"/>
    {/* Tick marks */}
    <line x1="14" y1="28" x2="16" y2="28" stroke="#E31837" strokeWidth="1.5" opacity=".4" strokeLinecap="round"/>
    <line x1="24" y1="12" x2="24" y2="14" stroke="#E31837" strokeWidth="1.5" opacity=".4" strokeLinecap="round"/>
    <line x1="34" y1="28" x2="32" y2="28" stroke="#E31837" strokeWidth="1.5" opacity=".4" strokeLinecap="round"/>
    {/* Bolt above */}
    <path d="M26 6 L22 14 H26 L22 22" stroke="#FCD34D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity=".9"/>
  </svg>
);

const PriceIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8">
    <circle cx="24" cy="24" r="19" fill="#FFF1F2" stroke="#E31837" strokeWidth="1.8"/>
    <path d="M24 10 L24 38" stroke="#E31837" strokeWidth="1.8" strokeLinecap="round" opacity=".3"/>
    <path d="M18 16 Q18 12 24 12 Q30 12 30 17 Q30 22 24 22 Q30 22 30 28 Q30 34 24 34 Q18 34 18 30"
      stroke="#E31837" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

const SupportIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8">
    <circle cx="24" cy="18" r="8" fill="#FFF1F2" stroke="#E31837" strokeWidth="1.8"/>
    <circle cx="24" cy="18" r="3.5" fill="#E31837" opacity=".35"/>
    <path d="M8 40 C8 31.2 15.2 24 24 24 C32.8 24 40 31.2 40 40"
      stroke="#E31837" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    {/* Headset addition */}
    <path d="M8 34 L6 34 Q4 34 4 36 L4 40 Q4 42 6 42 L9 42" stroke="#E31837" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity=".5"/>
    <path d="M40 34 L42 34 Q44 34 44 36 L44 40 Q44 42 42 42 L39 42" stroke="#E31837" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity=".5"/>
  </svg>
);

const features = [
  { Icon: QualityIcon, title: "OEM-Certified Quality", desc: "Every part meets or exceeds OEM specifications, verified before it ever reaches you." },
  { Icon: SpeedIcon,   title: "24-Hour Dispatch",     desc: "Order by 5 PM — your parts are dispatched the very next morning, nationwide." },
  { Icon: PriceIcon,   title: "Unbeatable Prices",    desc: "Direct from manufacturer to you — no middlemen, no markups, just great value." },
  { Icon: SupportIcon, title: "Expert Support",       desc: "Our automotive experts help you find exactly the right part for your make & year." },
];

const stats = [
  { value: "12+",  label: "Years in Business" },
  { value: "500+", label: "Parts Stocked" },
  { value: "10K+", label: "Customers Served" },
  { value: "99%",  label: "Satisfaction Rate" },
];

export default function About() {
  return (
    <section id="about" className="py-28 bg-white overflow-hidden" aria-label="About us">

      {/* ── Hero photo band ── */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-24">
        <div className="relative rounded-[2rem] overflow-hidden min-h-[320px] flex items-center">
          <div className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80')" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/92 via-gray-900/75 to-transparent" />

          {/* Diagonal red accent */}
          <div className="absolute top-0 right-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-red-600 to-transparent opacity-60" />

          <div className="relative z-10 px-10 py-14 max-w-xl">
            <span className="inline-flex items-center gap-2 bg-red-600/25 border border-red-500/40 text-red-400 text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-5">
              About SuperFast
            </span>
            <h2 className="text-4xl md:text-5xl text-white mb-5 leading-tight tracking-tight"
              style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800 }}>
              Driven by Quality,<br />
              <span className="text-red-500">Built for Speed.</span>
            </h2>
            <p className="text-white/55 leading-relaxed text-[0.97rem]">
              Founded with a simple mission — give every car owner access to genuine, premium auto
              parts without the dealership price tag or the wait time.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left: body copy + feature cards */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-gray-500 text-[1.02rem] leading-relaxed mb-10 max-w-lg">
              Whether you're a seasoned mechanic or a first-time DIYer, we have the parts you need —
              backed by genuine expertise and a seamless SMS-based enquiry process.
            </motion.p>

            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((f, i) => (
                <motion.div key={f.title}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.09 }}
                  className="group bg-gray-50 hover:bg-red-50 border border-gray-100 hover:border-red-100
                             rounded-2xl p-5 transition-all duration-300 cursor-default"
                >
                  <div className="mb-3"><f.Icon /></div>
                  <h3 className="text-gray-900 text-[0.95rem] mb-1.5 group-hover:text-red-700 transition-colors"
                    style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: stats grid + photo + badges */}
          <div className="space-y-6">
            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {stats.map((s, i) => (
                <div key={s.label}
                  className="bg-gray-900 rounded-2xl p-6 flex flex-col justify-between min-h-[110px]">
                  <span className="text-4xl text-red-500 block leading-none mb-1"
                    style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800 }}>{s.value}</span>
                  <span className="text-gray-400 text-sm">{s.label}</span>
                </div>
              ))}
            </motion.div>

            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden h-52"
            >
              <img
                src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=900&q=80"
                alt="Auto parts workshop" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5">
                <p className="text-white text-lg" style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>
                  Nationwide Delivery
                </p>
                <p className="text-white/65 text-sm">All 50 states covered</p>
              </div>
            </motion.div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2.5">
              {["ISO Certified", "OEM Approved", "5★ Rated", "Secure Checkout"].map(b => (
                <span key={b}
                  className="bg-white border border-gray-200 text-gray-500 text-xs font-semibold px-4 py-2 rounded-full shadow-sm">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
